import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { PHOTO_CONTROL_CLASSES } from './photoControls';

const VARIANT_CLASSES = {
  // Rows, cards and headers on a page or sheet. 8px, like the icon buttons
  // it sits beside in planner rows.
  default: 'rounded-lg text-content-muted hover:text-primary hover:bg-primary-lighter',
  // Over a photo (same look as the back/like/save/share controls)
  photo: `rounded-full ${PHOTO_CONTROL_CLASSES}`,
  // On a coloured fill: your own chat bubble, a dark overlay bar
  inverse: 'rounded-full text-content-inverse/70 hover:text-content-inverse hover:bg-content-inverse/15',
};

/**
 * MoreButton — the one "more" menu trigger (Kurtis, 2026-10-02). Its dots
 * follow the device: ⋯ on iPhone, iPad and Mac, ⋮ on Android and Windows (the
 * `more` icon decides; see Icon.js). A 44px tap area on phones.
 *
 * Where a "more" sits among sibling buttons that share their own look (the
 * feed card's action row, the profile's IconButtons, the day calendar's
 * glass chip), use `<Icon name="more">` inside that look instead.
 *
 * Props:
 * @param {Function} onClick
 * @param {'default'|'photo'|'inverse'} variant
 * @param {'md'|'sm'} size - icon size only
 * @param {boolean} compact - 28px from `sm` up, for dense desktop rows whose
 *   menus appear on hover (planner cards). Phones keep 44px.
 * @param {string} ariaLabel - Defaults to "More options". Pass a specific one
 *   where it helps ("Message options", "More actions for this booking").
 * @param {string} className - Placement (margins, absolute positioning)
 * @param {React.ReactNode} children - Extra marks inside the button (a dot badge)
 * Anything else (title, aria-haspopup, aria-expanded, onPointerDown) passes
 * through; a ref reaches the <button> (menus anchor to it).
 */
const MoreButton = forwardRef(function MoreButton({ onClick, variant = 'default', size = 'md', compact = false, ariaLabel = null, className = '', children, ...rest }, ref) {
  const { t } = useTranslation('common');
  // `relative` anchors a badge child, but Tailwind emits it after `absolute`,
  // so it would override a caller's own positioning (as in CloseButton)
  const positioned = /(^|\s)!?(absolute|fixed|sticky)(\s|$)/.test(className);
  const classes = [
    positioned ? '' : 'relative',
    'w-11 h-11 flex-shrink-0 inline-flex items-center justify-center transition-colors duration-fast',
    compact ? 'sm:w-7 sm:h-7' : '',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light',
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.default,
    className,
  ].filter(Boolean).join(' ');
  return (
    <button ref={ref} type="button" onClick={onClick} className={classes} aria-label={ariaLabel || t('controls.more')} {...rest}>
      <Icon name="more" className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
      {children}
    </button>
  );
});

export default MoreButton;
